import React, { useState, useEffect } from 'react';
import { useFavouriteQuestionsAtom } from '../../../atoms/chat/useFavouriteQuestionsAtom';
import { useSelectedTabAtom } from '../../../atoms/chat/useSelectedTabAtom';
import { saveFavouriteQuestion, getFavouriteQuestions } from '../../../api/questionAPIs';
import { useTranslation } from 'react-i18next';
import { useChatEnvironmentAtom } from '../../../atoms/chat/useChatEnvironmentAtom';
import { marked } from 'marked';

import MessageTable from './MessageTable';
import MessageChart from './MessageChart';
import MessageSourceModal from './MessageSourceModal';
import MessageRating from './MessageRating';
import MessageReview from './MessageReview';
import useAuth from "../../../hooks/useAuth";
import useCookie from '../../../hooks/useCookie';
import config from '../../../config/configJS';
import ImageGallery from '../image/ImageGallery';
import DOMPurify from 'dompurify';
import hljs from 'highlight.js';
import "highlight.js/styles/magula.css";

const MessageBubble = (props) => {
  const { auth } = useAuth();
  let userName = auth.ufn;
  const [t,] = useTranslation('global');
  const [ulaCookie,] = useCookie("ULA");

  const [chatEnvironmentId, ] = useChatEnvironmentAtom();
  const [, setFavQuestions] = useFavouriteQuestionsAtom();
  const [selectedTab] = useSelectedTabAtom();

  const [sourcesAvailable, setSourcesAvailable] = useState(true);
  const [showSources, setShowSources] = useState(false);
  
  const toggleSources = () => {
    setShowSources(!showSources);
  };

  const handleIconClick = () => {
    const lang = JSON.parse(ulaCookie);
    console.log('lang null', lang);

    saveFavouriteQuestion(selectedTab, props.message, lang, chatEnvironmentId)
    .then(() => {
      const lang = JSON.parse(ulaCookie);
      var questions = getFavouriteQuestions(selectedTab, lang);
      setFavQuestions(questions);
    })
  };

  useEffect(() => {
    if (props.meta && props.meta.length === 0) {    
      setSourcesAvailable(false);
    } 
    console.log('propsType: ', props.type);
  }, []);

  const processText = (text) => {
    if (text === undefined) {
      return '';
    }
    
    const textWithLinks = replaceHttpTextWithLink(text);
    
    const markdownHtml = marked.parse(textWithLinks);
    
    const sanitizedHtml = DOMPurify.sanitize(markdownHtml);
  
    // Trim any excessive whitespace around the html to prevent stretching
    return sanitizedHtml.trim();
  };

  const replaceHttpTextWithLink = (text) => {
    if (text == undefined) {
      return text;
    }

    // starting with http/https
    const regex1 = /((http|https):\/\/[^\s.]+(\.[^\s.]+)*)(\b|(?=\s)|(?=\.$))/ig;
    if (text.match(regex1)) {
      return text.replace(regex1, (url) => {
        return `<a href="${url}" target="_blank">${url}</a>`;
      });
    }

    //starting with www
    const regex2 = /(^|[^\/])(www\.[^\s.]+(\.[^\s.]+)*(\b|(?=\s)|$))/ig;
    if (text.match(regex2)) {
      return text.replace(regex2, (match, p1, url) => {
        return `${p1}<a href="https://${url}" target="_blank">${url}</a>`;
      });
    }
    
    return text;
  }

  const getFileNameFromUrl = (url) => {
    return url.substring(url.lastIndexOf('/') + 1);
  };

  function cleanSqlString(sqlString) {
    if(sqlString == undefined){
      return "";
    }
    // Regular expression to remove backticks, 'sql', and whitespace at the start and end
    const cleanedString = sqlString.replace(/```sql\s*|\s*```/g, '').trim();
    return cleanedString;
  }

  const highlightCode = (props) => {
      try {
          if (props.role === "assistant" && props.type === 0 && props.message !== undefined) {
              // Adjust regex to match all occurrences of the code block pattern
              const regex = /```(\w+)?\s*([\s\S]*?)\s*```/g;
              const matches = [...props.message.matchAll(regex)];
              
              if (matches.length === 0) {
                  // No code blocks, just process the entire message as markdown
                  return processText(props.message);
              }

              // Extract code blocks and replace them with placeholders
              let processedMessage = props.message;
              const codeBlocks = [];
              
              matches.forEach((match, index) => {
                  const placeholder = `CODE_BLOCK_PLACEHOLDER_${index}`;
                  codeBlocks.push({
                      placeholder: placeholder,
                      language: match[1] || '',
                      code: match[2],
                      original: match[0]
                  });
                  processedMessage = processedMessage.replace(match[0], placeholder);
              });
              
              // Process the text with markdown (excluding code blocks)
              let markdownProcessed = processText(processedMessage);
              
              // Replace placeholders with highlighted code blocks
              codeBlocks.forEach((block) => {
                  const highlightedCode = DOMPurify.sanitize(hljs.highlightAuto(block.code).value);
                  markdownProcessed = markdownProcessed.replace(
                      block.placeholder, 
                      `<pre><code>${highlightedCode}</code></pre>`
                  );
              });
              
              return markdownProcessed;
          } else {
              return processText(props.message);
          }
      } catch (ex) {
          console.error("Error processing code highlighting:", ex);
          return processText(props.message);
      }
  };

  return (
    <>
      <div className={`msg ${props.role === 'assistant' ? 'left-msg' : 'right-msg'}`}>
        <div>
          <img
            alt={props.role}
            src={props.role === 'assistant' ? props.bot : props.avatar}
            className="msg-img"
          />
        </div>

        <div className="msg-bubble">
          {props.type !== 'loading' ? (
            <div className="msg-info">
              <div className="msg-info-name">
                {props.role === 'assistant' ? 'Millada' : props.role === 'user' ? userName : null}
              </div>
              <div className="msg-favorite" onClick={handleIconClick}>
                {props.role === 'assistant' ? null : <span className="material-symbols-outlined">add_circle</span>}
              </div>
              <div className="msg-info-time">{props.sent}</div>
            </div>
          ) : null}
          { props.type === 0 || props.type === 4 || props.type === 'stopping' ? (
                props.role === "assistant" ? (
                <>
                  <div className="chat-text" dangerouslySetInnerHTML={{__html: highlightCode(props) }}/>
                  {props.generatedSQL && <div className="sql-wrapper">{cleanSqlString(props.generatedSQL)}</div>}
                </>
              ) : (
                <div dangerouslySetInnerHTML={{__html: processText(props.message) }}/>
              )
          ) : props.type === 1 ? (
            <MessageTable id={props.id} columns={props.message.Columns} rows={props.message.Rows} generatedSQL={props.generatedSQL} />
          ) : props.type === 2 ? (
            <MessageChart id={props.id} json={props.message} /> 
          ): props.type === 3 ? (           
            <>
            <div className="msg-text-container">
              <div className="msg-text"
                  dangerouslySetInnerHTML={{__html: processText(props.message)}}>
              </div>
            </div>          

            {config.imgAllowed ?
             <div className="img-container">
              {props.meta?.[0]?.Metadata?.page_image_guids?.length > 0 && (
                <ImageGallery idString={props.meta[0].Metadata.page_image_guids} />
              )}
             </div> 
            : null }

            {sourcesAvailable && (
              <>
              <div className="show-sources">
              <div className="show-sources-label" onClick={toggleSources}>{t('showSources')}</div>
                <span className={showSources ? "material-symbols-outlined show-sources-icon rotate0 rotate180" : "material-symbols-outlined show-sources-icon rotate0"} onClick={toggleSources}>
                keyboard_arrow_down
                </span>
              </div>

              <div className={showSources ? "sources-list height8em" : "height0"}>
                {showSources && (
                  props.meta.map((source, index) => 
                    <div className='msg-source-item' key={index}>
                        <MessageSourceModal 
                          meta={{
                            f: props.envType === 'S' ? getFileNameFromUrl(source.Metadata.url_source) : source.Metadata.f,
                            n: source.Metadata.n,
                            p: source.Metadata.p,
                            c: source.Chunk,
                            s: props.envType === 'S' ?  source.Metadata.url_source + '?web=1' : null
                          }} 
                          extractionTaskId={source.ExtractionTaskId}
                        />
                    </div>
                ))}
              </div>
              </>
            )} 

            <MessageRating id={props.messageId} />
            {config.msgReviewsAllowed ? 
              <MessageReview id={props.messageId} />
            : null }
            </>
          ) : props.type === 'loading' ? (
            <div className="msg-text">
              <div className="typing">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          ) : null }
        </div>
      </div>
    </>
  );
};

export default MessageBubble;
